
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PlannedFeature, allPlannedFeatures } from "@/data/knowledge-base/planned-features";
import { Feature, currentFeatures } from "@/data/knowledge-base/current-features";
import {
  Check, Blocks, Filter, LineChart, Laptop, LayoutGrid,
  BookOpen, User, FileText, ChevronDown, Database,
  Search, ArrowLeftRight, GitBranch, Languages, MessageSquare,
  Settings, HelpCircle, MessagesSquare,
  Award, ImageIcon, Eye, TestTube2
} from "lucide-react";

interface FeatureCardProps {
  feature: Feature | PlannedFeature;
  isImplemented: boolean;
}

const getIconForCategory = (category: string) => {
  const iconMap: Record<string, React.ReactNode> = {
    "core": <Blocks className="h-5 w-5" />,
    "filtering": <Filter className="h-5 w-5" />,
    "metrics": <LineChart className="h-5 w-5" />,
    "display": <Laptop className="h-5 w-5" />,
    "tutorials": <BookOpen className="h-5 w-5" />,
    "user": <User className="h-5 w-5" />,
    "parts": <FileText className="h-5 w-5" />,
    "knowledge-base": <Database className="h-5 w-5" />,
    "accessibility": <Languages className="h-5 w-5" />,
    "interactive": <MessageSquare className="h-5 w-5" />,
    "community": <MessagesSquare className="h-5 w-5" />,
  };

  return iconMap[category] || <HelpCircle className="h-5 w-5" />;
};

const FeatureCard: React.FC<FeatureCardProps> = ({ feature, isImplemented }) => {
  // Get badge text and style
  const getBadgeText = () => {
    if (isImplemented) return "Implemented";
    if ("status" in feature) {
      switch (feature.status) {
        case "in-progress": return "In Progress";
        case "planned": return "Planned";
        default: return "Planned";
      }
    }
    return "Planned";
  };

  const getBadgeStyle = () => {
    if (isImplemented) {
      return "bg-green-700 text-white hover:bg-green-800";
    }
    if ("status" in feature && feature.status === "in-progress") {
      return "bg-yellow-600 text-white hover:bg-yellow-700";
    }
    return "bg-gray-500 text-white hover:bg-gray-600";
  };

  return (
    <Card className="h-full transition-all hover:shadow-md">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-3">
            <div className="mt-1 text-primary">
              {getIconForCategory(feature.category)}
            </div>
            <div>
              <CardTitle className="text-lg">{feature.name}</CardTitle>
              <CardDescription className="mt-1">{feature.category}</CardDescription>
            </div>
          </div>
          <Badge className={getBadgeStyle()}>
            {getBadgeText()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">{feature.description}</p>
        {("priority" in feature) && (
          <div className="mt-4">
            <Badge variant="outline" className="mr-2">
              Priority: {feature.priority}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface FeatureRoadmapProps {
  showImplemented?: boolean;
  showPlanned?: boolean;
  categoryFilter: string | null;
}

const FeatureRoadmap: React.FC<FeatureRoadmapProps> = ({ 
  showImplemented = true, 
  showPlanned = true,
  categoryFilter = null 
}) => {
  const filteredCurrentFeatures = categoryFilter 
    ? currentFeatures.filter(feature => feature.category === categoryFilter)
    : currentFeatures;
    
  const filteredPlannedFeatures = categoryFilter 
    ? allPlannedFeatures.filter(feature => feature.category === categoryFilter)
    : allPlannedFeatures;

  return (
    <div className="space-y-12">
      {showImplemented && filteredCurrentFeatures.length > 0 && (
        <section>
          <h2 className="text-2xl font-bold mb-6">Implemented Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredCurrentFeatures.map(feature => (
              <FeatureCard key={feature.id} feature={feature} isImplemented={true} />
            ))}
          </div>
        </section>
      )}

      {showPlanned && filteredPlannedFeatures.length > 0 && (
        <section>
          <h2 className="text-2xl font-bold mb-6">Planned Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredPlannedFeatures.map(feature => (
              <FeatureCard key={feature.id} feature={feature} isImplemented={false} />
            ))}
          </div>
        </section>
      )}
      
      {/* Show an empty state when no features match the filters */}
      {((!showImplemented || filteredCurrentFeatures.length === 0) && 
         (!showPlanned || filteredPlannedFeatures.length === 0)) && (
        <div className="py-12 text-center">
          <h3 className="text-xl font-medium text-muted-foreground">No features match your current filters</h3>
          <p className="text-muted-foreground mt-2">Try changing your filter selections</p>
        </div>
      )}
    </div>
  );
};

export default FeatureRoadmap;
